import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { debounce } from "@/utils/debounce";
import { AutoComplete, Card } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { MAP_API_CONFIG } from "../../types/map";
import { defineAddressByCoordinates, findCoordinates, searchAddresses, } from "./utils";
// --- функция для единой загрузки API ---
let ymapsPromise = null;
function loadYandexMaps() {
    // If ymaps is already loaded, wrap it in a promise that resolves to itself
    if (window.ymaps && window.ymaps.ready) {
        return new Promise((resolve) => {
            window.ymaps.ready(() => resolve(window.ymaps));
        });
    }
    if (!ymapsPromise) {
        ymapsPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = `https://api-maps.yandex.ru/2.1/?apikey=${MAP_API_CONFIG.javascriptApiKey}&lang=ru_RU`;
            script.onerror = () => reject(new Error("Failed to load Yandex Maps API"));
            script.onload = () => {
                if (window.ymaps && window.ymaps.ready) {
                    window.ymaps.ready(() => {
                        resolve(window.ymaps);
                    });
                }
                else {
                    reject(new Error("Yandex Maps API not available"));
                }
            };
            document.head.appendChild(script);
        });
    }
    return ymapsPromise;
}
export const AddressSelector = ({ value, onChange, disabled = false, height = 300, }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const marker = useRef(null);
    const [suggestions, setSuggestions] = useState([]);
    const [searchValue, setSearchValue] = useState(value?.addressShort || "");
    // --- обновление адреса ---
    const updateAddress = async (lat, lng, address) => {
        if (disabled)
            return;
        if (!address) {
            const result = await defineAddressByCoordinates([lat, lng]);
            address = result.addressShort || `${lat}, ${lng}`;
        }
        onChange?.({
            latitude: lat,
            longitude: lng,
            addressShort: address,
            addressLocality: address,
        });
    };
    // --- инициализация карты ---
    useEffect(() => {
        let destroyed = false;
        loadYandexMaps()
            .then((ymaps) => {
            if (!mapRef.current || destroyed)
                return;
            if (!ymaps || !ymaps.Map) {
                console.error("ymaps.Map is not available");
                return;
            }
            // Yandex Maps использует формат [долгота, широта]
            const defaultCenterValue = value?.latitude && value?.longitude
                ? [value.latitude, value.longitude]
                : [55.7558, 37.6176];
            const center = defaultCenterValue;
            try {
                // уникальная карта в каждом контейнере
                mapInstance.current = new ymaps.Map(mapRef.current, {
                    center,
                    zoom: 10,
                    controls: ["zoomControl"],
                });
                marker.current = new ymaps.Placemark(center, {}, {
                    draggable: !disabled,
                    preset: "islands#redIcon",
                });
                mapInstance.current.geoObjects.add(marker.current);
                // if (!disabled) {
                //   const handleMapClick = async (coords: number[]) => {
                //     console.log(coords);
                //     await updateAddress(coords[1], coords[0]);
                //   };
                //   marker.current.events.add("dragend", () => {
                //     handleMapClick(marker.current.geometry.getCoordinates());
                //   });
                //   mapInstance.current.events.add("click", (e: any) => {
                //     handleMapClick(e.get("coords"));
                //   });
                // }
            }
            catch (error) {
                console.error("Error creating map:", error);
            }
        })
            .catch((error) => {
            console.error("Error loading Yandex Maps:", error);
        });
        return () => {
            destroyed = true;
            // каждая карта уничтожается отдельно
            mapInstance.current?.destroy();
            mapInstance.current = null;
        };
    }, [disabled]);
    // --- обновление при изменении value ---
    useEffect(() => {
        setSearchValue(value?.addressShort || "");
        if (marker.current && value) {
            // Yandex Maps использует формат [долгота, широта]
            const coords = [value.latitude, value.longitude];
            marker.current.geometry.setCoordinates(coords);
            mapInstance.current?.setCenter(coords, 15);
        }
    }, [value]);
    const delayedSearch = useCallback(debounce(async (query) => {
        const results = await searchAddresses(query);
        // гарантируем уникальные ключи
        const unique = results.map((item, index) => ({
            ...item,
            key: `${item.value}-${index}`,
        }));
        setSuggestions(unique);
    }, 300), []);
    const handleSearch = useCallback((query) => {
        setSearchValue(query);
        delayedSearch(query);
    }, [delayedSearch]);
    const handleSelect = async (address) => {
        if (disabled)
            return;
        const coordinates = await findCoordinates(address);
        if (coordinates) {
            const [lat, lng] = coordinates;
            // Yandex Maps использует формат [долгота, широта]
            const coords = [lng, lat];
            marker.current?.geometry.setCoordinates(coords);
            mapInstance.current?.setCenter(coords, 15);
            await updateAddress(lat, lng, address);
            setSearchValue(address);
        }
    };
    return (_jsxs(Card, { title: "\u0412\u044B\u0431\u043E\u0440 \u0430\u0434\u0440\u0435\u0441\u0430", size: "small", children: [_jsx(AutoComplete, { value: searchValue, options: suggestions.map((s, i) => ({
                    value: s.value,
                    label: s.label || s.value,
                    key: s.key || `${s.value}-${i}`, // защита от дублей
                })), onSearch: handleSearch, onSelect: handleSelect, placeholder: "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0430\u0434\u0440\u0435\u0441 \u0434\u043B\u044F \u043F\u043E\u0438\u0441\u043A\u0430...", style: { width: "100%", marginBottom: 16 }, disabled: disabled }), _jsx("div", { ref: mapRef, style: {
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    minHeight: 400,
                } })] }));
};
AddressSelector.displayName = "AddressSelector";
